/**
 * Build a weather line for injection into the LLM traveller summary.
 * Keeps it concise — the concierge persona decides what to do with it.
 * Transport-aware: walking in rain is a much bigger deal than taxi in rain.
 */

import type { WeatherData } from '@/hooks/useWeather';

export function buildWeatherLine(
  weather: WeatherData | null,
  timeAvailable: string,
  transport?: string
): string | null {
  if (!weather) return null;

  const { temperature, apparentTemperature, label, emoji, windSpeed, precipitation, prefersIndoor } = weather;

  // Feels-like only if noticeably different (±3°C)
  const feelsLike = Math.abs(temperature - apparentTemperature) >= 3
    ? ` (feels like ${apparentTemperature}°C)`
    : '';

  // Wind only if notable
  const wind = windSpeed >= 20 ? `, wind ${windSpeed} km/h` : '';

  // Active precipitation
  const rain = precipitation > 0 ? `, ${precipitation} mm precipitation right now` : '';

  // Core line
  let line = `${emoji} ${temperature}°C${feelsLike}, ${label.toLowerCase()}${wind}${rain}`;

  // Transport-specific weather impact
  if (prefersIndoor && transport === 'walking') {
    line += ' — walking in this weather is unpleasant, prioritise closer and covered/indoor options';
  } else if (prefersIndoor && transport === 'transit') {
    line += ' — waiting at stops in this weather is unpleasant, consider indoor destinations';
  } else if (prefersIndoor) {
    line += ' — indoor or covered options may be more comfortable';
  }

  // Extreme heat/cold for walkers
  if (transport === 'walking') {
    if (apparentTemperature >= 35) {
      line += ' — very hot for walking, suggest shorter distances and shaded/air-conditioned spots';
    } else if (apparentTemperature <= 2) {
      line += ' — very cold for walking, suggest closer options and warm indoor spaces';
    }
  }

  // For multi-day trips, caveat that weather will change
  if (timeAvailable === '2-3-days') {
    line += ' (current conditions — weather will change over your trip)';
  }

  return line;
}
